import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "./useApi";
import { QUEUE_DETAILS_URL, buildQueue } from "../components/socQueue";

// Loads the Security Operations queue from one read, GET /unifiedRisks/details (its risk types, open
// only, Defender XDR incidents and alerts only). `defenderConnected: false` (the page passes it when the
// summary says Defender XDR isn't connected, so the summary counts no incidents or alerts) leaves the
// Defender rows out and sets `defenderUnavailable`, so the page can say why none are listed.
// `reload()` resolves to the freshly built rows, or null when the read failed or was skipped or
// superseded. It is how the page shows a write's result: the queue reloads after every write.
// `reload({ silent: true })` is the page's automatic refresh: rows stay on screen (no loading state),
// an auth error never redirects (background polling), and a failed read keeps the last good rows.
// An automatic refresh is skipped while another read is still running: when a read takes longer than
// the refresh interval, superseding it every minute would discard every response and the queue would
// never update.
// `changeKey` moves on every reload that isn't the automatic refresh (after a write, Refresh, Retry),
// when its rows land: the page re-reads other copies of the same risks (the full risk details) when it
// changes. An automatic refresh leaves it alone; the queue re-reads an open row's details itself when
// that refresh changed the row's owner, status or tickets.
export const useSocQueueData = ({ enabled = true, defenderConnected = true } = {}) => {
  const apiCall = useApi();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  const [changeKey, setChangeKey] = useState(0);
  const seqRef = useRef(0);
  const inFlightRef = useRef(0);

  const reload = useCallback(async ({ silent = false } = {}) => {
    if (silent && inFlightRef.current > 0) return null;
    seqRef.current += 1;
    const seq = seqRef.current;
    inFlightRef.current += 1;
    if (!silent) {
      setLoading(true);
      setError(false);
    }
    let details;
    let failed = false;
    try {
      details = await apiCall("GET", QUEUE_DETAILS_URL, null, false, null, silent);
    } catch {
      failed = true;
    } finally {
      inFlightRef.current -= 1;
    }
    if (seq !== seqRef.current) return null;
    // Moved with the rows it answers for (same render), so a consumer comparing rows can tell a
    // reload's own change from a silent refresh's.
    if (!silent) setChangeKey((k) => k + 1);
    if (failed) {
      if (silent) return null;
      setError(true);
      setAllRows([]);
      setLoading(false);
      return null;
    }
    const built = buildQueue(Array.isArray(details) ? details : [], new Date());
    setError(false);
    setAllRows(built);
    setLoading(false);
    return built;
  }, [apiCall]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  const rows = useMemo(
    () => (defenderConnected ? allRows : allRows.filter((r) => r.area !== "incidents")),
    [allRows, defenderConnected]
  );

  return { rows, loading, error, defenderUnavailable: !defenderConnected, changeKey, reload };
};

export default useSocQueueData;
